import React from "react";
import { Tooltip } from "@mui/material";
import styled from "styled-components";

export type FieldHelpContent = {
  title: string;
  body: string;
  notes?: string[];
  examples?: string[];
};

export const FIELD_HELP = {
  viewDisplayConditions: {
    title: "画面の表示条件",
    body:
      "この画面を表示できる条件を 1 行 1 条件で入力します。画面内の一部品だけの条件ではなく、画面全体が表示対象になる条件を書いてください。",
    notes: [
      "TestDesigner では条件文に含まれるキーワードから、認証・権限・一般的な表示条件のプロパティを補助分類します。",
      "認証系は「ログイン」「未ログイン」「認証」「未認証」「セッション」を含めると判定しやすくなります。",
      "権限系は「権限」「ロール」「管理者」「一般ユーザー」「会員種別」「会員ランク」を含めると判定しやすくなります。",
      "その他条件は「初回」「期間」「確率」「広告」「ABテスト」「ステータス」などのキーワードを含めると判定しやすくなります。",
    ],
    examples: [
      "ログイン状態の場合",
      "管理者ロールの場合",
      "初回ログイン時のみ",
      "広告表示対象の場合",
    ],
  },
  internalProcess: {
    title: "内部処理",
    body:
      "ページ遷移を伴なわないアクションです。同一画面上で起きる処理（計算・表示更新・非同期通信など）を、自動テストが追えるよう発生順に登録します。",
  },
  itemRequiredField: {
    title: "必須項目",
    body:
      "ユーザーが適切な値を入力・選択しない限り、フォームの送信や処理の進行を許可しない場合にチェックしてください。",
  },
  transitionSection: {
    title: "遷移",
    body:
      "ページ遷移を伴うアクションです。操作の結果として別画面へ移るケースを登録します。",
  },
  maskType: {
    title: "マスク種別",
    body:
      "リグレッションテスト実行時に、この要素をキャプチャからどう隠すかを指定します。",
    notes: [
      "cover は色付きボックスで塗りつぶします。",
      "hide は visibility: hidden で領域を残して透明化します。",
      "remove は display: none で DOM から除去します。後続要素が詰まって表示されます。",
      "なしを選ぶとマスクしません。",
    ],
  },
} satisfies Record<string, FieldHelpContent>;

type FieldHelpButtonProps = {
  help: FieldHelpContent;
  ariaLabel: string;
};

export function FieldHelpButton({ help, ariaLabel }: FieldHelpButtonProps) {
  return (
    <Tooltip title={<FieldHelpTooltipContent help={help} />} arrow>
      <HelpButton type="button" aria-label={ariaLabel}>
        ?
      </HelpButton>
    </Tooltip>
  );
}

function FieldHelpTooltipContent({ help }: { help: FieldHelpContent }) {
  return (
    <TooltipContent>
      <TooltipTitle>{help.title}</TooltipTitle>
      <TooltipBody>{help.body}</TooltipBody>
      {help.notes && help.notes.length > 0 ? (
        <TooltipList>
          {help.notes.map((note) => (
            <li key={note}>{note}</li>
          ))}
        </TooltipList>
      ) : null}
      {help.examples && help.examples.length > 0 ? (
        <ExamplesBlock>
          <ExamplesTitle>入力例</ExamplesTitle>
          <TooltipList>
            {help.examples.map((example) => (
              <li key={example}>{example}</li>
            ))}
          </TooltipList>
        </ExamplesBlock>
      ) : null}
    </TooltipContent>
  );
}

const HelpButton = styled.button`
  width: 22px;
  height: 22px;
  border-radius: 50%;
  border: 1px solid #d1d5db;
  background: #f9fafb;
  color: #6b7280;
  font-size: 0.75rem;
  font-weight: 700;
  cursor: help;
  padding: 0;
  line-height: 1;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: #e5e7eb;
    color: #374151;
  }
`;

const TooltipContent = styled.div`
  max-width: 360px;
  font-size: 0.8125rem;
  line-height: 1.55;
`;

const TooltipTitle = styled.div`
  font-weight: 700;
  margin-bottom: 4px;
`;

const TooltipBody = styled.div`
  margin-bottom: 6px;
`;

const TooltipList = styled.ul`
  margin: 0;
  padding-left: 18px;
`;

const ExamplesBlock = styled.div`
  margin-top: 6px;
`;

const ExamplesTitle = styled.div`
  font-weight: 700;
  margin-bottom: 2px;
`;
